import { Box } from "@mui/material";
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { prefersReducedMotion } from "../siteKit/motion/reducedMotion";
import { revealRef } from "../siteKit/motion/reveal";
import { createGuideEngine, type GuideEngine } from "./guideEngine";
import { GUIDE_PEEK_SIZE, GUIDE_STYLES } from "./guideStyles";
import {
  GUIDE_SCENE,
  MANUAL_GUIDES,
  type ManualGuideKey,
} from "./manualGuides";
import { guideStartStepFromLocation } from "./manualJump";
import { manualGuideFrameUrl, manualGuidePreview } from "./manualMedia";

export interface GuideStepText {
  title: string;
  caption: string;
}

/** 무대에 들어오면 자동 재생, 벗어나면 멈춤 — 화면보다 조금 넓게 본다 */
const VISIBLE_OPTIONS: IntersectionObserverInit = {
  rootMargin: "80px 0px",
  threshold: 0.45,
};
/** 화면 이만큼 앞에서 첫 그림을 미리 받는다(보통 우선순위 — 지나치는 안내가 지금 보는 안내의 그림 받기를 밀어내지 않게) */
const NEAR_ROOT_MARGIN = "600px 0px";
/** 좌우로 이만큼 밀면 이전·다음(세로로 밀면 페이지 스크롤 그대로) */
const SWIPE_MIN_PX = 40;
const SWIPE_SLOPE = 1.3;
/** 밀기 뒤 따라오는 click 을 재생 전환으로 받지 않는 시간 */
const SWIPE_CLICK_GUARD_MS = 450;

interface GuideWatch {
  visible: (visible: boolean) => void;
  near: () => void;
}

const watches = new WeakMap<Element, GuideWatch>();
let visibleObserver: IntersectionObserver | null = null;
let nearObserver: IntersectionObserver | null = null;

/** 안내 서른 개가 관찰자 둘을 함께 쓴다 */
function watchGuide(element: Element, watch: GuideWatch) {
  if (typeof IntersectionObserver === "undefined") return () => {};
  visibleObserver ??= new IntersectionObserver(
    (entries) =>
      entries.forEach((entry) =>
        watches.get(entry.target)?.visible(entry.isIntersecting)
      ),
    VISIBLE_OPTIONS
  );
  nearObserver ??= new IntersectionObserver(
    (entries) =>
      entries.forEach((entry) => {
        if (entry.isIntersecting) watches.get(entry.target)?.near();
      }),
    { rootMargin: NEAR_ROOT_MARGIN }
  );
  const visible = visibleObserver;
  const near = nearObserver;
  watches.set(element, watch);
  visible.observe(element);
  near.observe(element);
  return () => {
    visible.unobserve(element);
    near.unobserve(element);
    watches.delete(element);
  };
}

const CURSOR_ICON = (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
    <path
      d="M5 3l14 8.5-6.2 1.3L9.6 19z"
      fill="#fff"
      stroke="#111"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

/** 오른쪽 버튼 단계의 커서 옆 표시 — 오른쪽 버튼 칸만 칠한 마우스(색은 스타일의 currentColor) */
const RIGHT_BUTTON_ICON = (
  <svg
    data-guide-button=""
    viewBox="0 0 12 16"
    width="12"
    height="16"
    aria-hidden="true"
  >
    <rect
      x="0.8"
      y="0.8"
      width="10.4"
      height="14.4"
      rx="5.2"
      fill="#fff"
      stroke="#111"
      strokeWidth="1.3"
    />
    <path d="M6 0.8A5.2 5.2 0 0 1 11.2 6V6.6H6z" fill="currentColor" />
    <path d="M6 0.8V6.6M0.8 6.6H11.2" stroke="#111" strokeWidth="1.1" />
  </svg>
);

const PauseIcon = ({ size, kind }: { size: number; kind?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    aria-hidden="true"
    data-icon-kind={kind}
  >
    <rect x="3.5" y="2.5" width="3" height="11" rx="1" fill="currentColor" />
    <rect x="9.5" y="2.5" width="3" height="11" rx="1" fill="currentColor" />
  </svg>
);

const PlayIcon = ({ size, kind }: { size: number; kind?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    aria-hidden="true"
    data-icon-kind={kind}
  >
    <path
      d="M4.5 2.8v10.4a.8.8 0 0 0 1.2.7l8.4-5.2a.8.8 0 0 0 0-1.4L5.7 2.1a.8.8 0 0 0-1.2.7z"
      fill="currentColor"
    />
  </svg>
);

const ArrowIcon = ({ direction }: { direction: "prev" | "next" }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d={
        direction === "prev" ? "M10 3.5 5.5 8l4.5 4.5" : "M6 3.5 10.5 8 6 12.5"
      }
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface Peek {
  index: number;
  left: number;
  url: string;
}

/** 미리보기 칸 안 배치 — 단계 확대 영역(없으면 장면 전체)을 칸에 담고, 누르는 자리에 파란 상자 */
function peekLayout(guideKey: ManualGuideKey, index: number) {
  const frame = MANUAL_GUIDES[guideKey].frames[index];
  const [vx, vy, vw, vh] = frame.view ?? [
    0,
    0,
    GUIDE_SCENE.width,
    GUIDE_SCENE.height,
  ];
  const scale = Math.min(
    GUIDE_PEEK_SIZE.width / vw,
    GUIDE_PEEK_SIZE.height / vh
  );
  const x = (GUIDE_PEEK_SIZE.width - vw * scale) / 2 - vx * scale;
  const y = (GUIDE_PEEK_SIZE.height - vh * scale) / 2 - vy * scale;
  const rect = frame.act?.rect;
  return {
    image: {
      width: GUIDE_SCENE.width * scale,
      height: GUIDE_SCENE.height * scale,
      left: x,
      top: y,
    },
    box: rect
      ? {
          left: x + rect[0] * scale - 3,
          top: y + rect[1] * scale - 3,
          width: rect[2] * scale + 6,
          height: rect[3] * scale + 6,
        }
      : null,
  };
}

export interface GuidePlayerProps {
  guideKey: ManualGuideKey;
  /** 절 제목 — 플레이어 이름 */
  title: string;
  steps: readonly GuideStepText[];
  /** 절의 「참고」 글 — 플레이어 아래(가로 터치 화면은 오른쪽 칸 재생 버튼 아래)에 둔다 */
  aside?: ReactNode;
}

/**
 * 단계별 안내 플레이어(service-manual): 무대 · 단계 막대 · 지금 단계 · 재생/일시정지 + 보조 기술용 단계 목록.
 * 그리기는 `guideEngine` 이 하고, 이 컴포넌트는 틀과 드물게 바뀌는 값(단계 번호·재생 여부·첫 그림 준비)만 다시 그린다.
 * 이전·다음은 무대 양옆 화살표(마우스로는 무대에 올렸을 때만 보임), 휴대폰은 그림을 좌우로 밀어도 되고, 키보드는 ←/→ 와 스페이스.
 * `/manual?step=N#<절>` 로 연 절은 N단계부터 재생한다(업데이트 소식의 「시연 영상 보기」).
 */
export function GuidePlayer({
  guideKey,
  title,
  steps,
  aside,
}: GuidePlayerProps) {
  const { t } = useTranslation("landing");
  const rootRef = useRef<HTMLElement | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const flashRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<GuideEngine | null>(null);
  const swipeRef = useRef<{ x: number; y: number } | null>(null);
  const swipedUntilRef = useRef(0);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [peek, setPeek] = useState<Peek | null>(null);
  // 카드를 숨길 때도 마지막 단계 내용은 남겨 둔다 — 서서히 사라지는 동안 빈 카드가 보이지 않게
  const [peekShown, setPeekShown] = useState(false);
  const guide = MANUAL_GUIDES[guideKey];
  const largestWidth = guide.widths[guide.widths.length - 1];
  const lastStep = steps.length - 1;
  const preview = manualGuidePreview(guideKey);

  useEffect(() => {
    const root = rootRef.current;
    const stage = stageRef.current;
    const view = viewRef.current;
    const image = imageRef.current;
    const ring = ringRef.current;
    const cursor = cursorRef.current;
    const flash = flashRef.current;
    const nav = navRef.current;
    if (
      !root ||
      !stage ||
      !view ||
      !image ||
      !ring ||
      !cursor ||
      !flash ||
      !nav
    )
      return undefined;
    const engine = createGuideEngine({
      elements: {
        stage,
        view,
        image,
        ring,
        cursor,
        flash,
        bars: () => [...nav.querySelectorAll<HTMLElement>("[data-guide-bar]")],
      },
      guide,
      frameUrl: (index, width) => manualGuideFrameUrl(guideKey, index, width),
      reducedMotion: prefersReducedMotion(),
      startIndex: guideStartStepFromLocation(guideKey, guide.frames.length),
      // 단계 막대·자막은 그림이 바뀌는 그 순간 함께 바뀌어야 해서 바로 그린다
      onStep: (index) => flushSync(() => setStep(index)),
      onPlaying: (value) => flushSync(() => setPlaying(value)),
      onReady: () => flushSync(() => setReady(true)),
    });
    engineRef.current = engine;
    const stopWatching = watchGuide(root, {
      visible: (visible) => engine.setVisible(visible),
      near: () => engine.prefetch(),
    });
    return () => {
      stopWatching();
      engine.dispose();
      engineRef.current = null;
    };
  }, [guide, guideKey]);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      engineRef.current?.seekBy(event.key === "ArrowRight" ? 1 : -1);
    } else if (
      (event.key === " " || event.key === "Spacebar") &&
      event.target === event.currentTarget
    ) {
      event.preventDefault();
      engineRef.current?.toggle(false);
    }
  };

  const onStagePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse") return;
    swipeRef.current = { x: event.clientX, y: event.clientY };
  };

  const onStagePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (
      Math.abs(dx) > SWIPE_MIN_PX &&
      Math.abs(dx) > Math.abs(dy) * SWIPE_SLOPE
    ) {
      swipedUntilRef.current = performance.now() + SWIPE_CLICK_GUARD_MS;
      engineRef.current?.seekBy(dx < 0 ? 1 : -1);
    }
  };

  const onStageClick = () => {
    if (performance.now() < swipedUntilRef.current) {
      swipedUntilRef.current = 0;
      return;
    }
    engineRef.current?.toggle(true);
  };

  const seekByArrow = (event: MouseEvent<HTMLElement>, delta: 1 | -1) => {
    const target = step + delta;
    const disablesThisArrow = delta < 0 ? target <= 0 : target >= lastStep;
    const hadFocus = document.activeElement === event.currentTarget;
    engineRef.current?.seekBy(delta);
    // 끝 단계에 닿아 이 화살표가 비활성이 되면, 초점이 문서 처음으로 튀지 않게 플레이어로 옮긴다
    if (disablesThisArrow && hadFocus)
      rootRef.current?.focus({ preventScroll: true });
  };

  const showPeek = (event: PointerEvent<HTMLLIElement>, index: number) => {
    const nav = navRef.current;
    const engine = engineRef.current;
    if (event.pointerType !== "mouse" || !nav || !engine) return;
    const navRect = nav.getBoundingClientRect();
    const itemRect = event.currentTarget.getBoundingClientRect();
    const center = itemRect.left - navRect.left + itemRect.width / 2;
    const left = Math.max(
      0,
      Math.min(
        navRect.width - GUIDE_PEEK_SIZE.frame,
        center - GUIDE_PEEK_SIZE.frame / 2
      )
    );
    setPeek({ index, left, url: engine.peekUrl(index) });
    setPeekShown(true);
  };

  const peekView = peek ? peekLayout(guideKey, peek.index) : null;
  const playLabel = t("site.manual.player.play");
  const pauseLabel = t("site.manual.player.pause");

  return (
    <Box
      component="figure"
      ref={(element: HTMLElement | null) => {
        rootRef.current = element;
        return revealRef(element);
      }}
      data-reveal="up"
      tabIndex={0}
      aria-label={t("site.manual.player.label", { title })}
      data-ready={ready ? "" : undefined}
      data-playing={playing ? "" : undefined}
      onKeyDown={onKeyDown}
      sx={GUIDE_STYLES.root}
    >
      <Box sx={GUIDE_STYLES.main}>
        <Box sx={GUIDE_STYLES.stageWrap}>
          <Box
            ref={stageRef}
            data-guide-stage=""
            sx={GUIDE_STYLES.stage}
            style={preview ? { backgroundImage: `url(${preview})` } : undefined}
            onClick={onStageClick}
            onPointerDown={onStagePointerDown}
            onPointerUp={onStagePointerUp}
            onPointerCancel={() => {
              swipeRef.current = null;
            }}
            // 안내 그림은 오른쪽 메뉴로 저장하거나 끌어 옮기지 못하게 한다(service-manual 「로그인하지 않은 방문자의 매뉴얼」)
            onContextMenu={(event) => event.preventDefault()}
          >
            <Box ref={viewRef} sx={GUIDE_STYLES.view}>
              {/* eslint-disable-next-line @next/next/no-img-element -- 사이트 미디어는 파이프라인이 만든 해시 파일을 그대로 쓴다(next/image 최적화 대상 아님) */}
              <img
                ref={imageRef}
                data-guide-layer=""
                draggable={false}
                width={largestWidth}
                height={Math.round(
                  (largestWidth * GUIDE_SCENE.height) / GUIDE_SCENE.width
                )}
                alt={steps[step]?.caption ?? ""}
                decoding="async"
              />
              <Box ref={ringRef} data-guide-layer="" sx={GUIDE_STYLES.ring} />
              <Box
                ref={cursorRef}
                data-guide-layer=""
                data-guide-cursor=""
                sx={GUIDE_STYLES.cursor}
              >
                {CURSOR_ICON}
                {RIGHT_BUTTON_ICON}
              </Box>
            </Box>
            <Box ref={flashRef} sx={GUIDE_STYLES.flash}>
              <PauseIcon size={26} kind="pause" />
              <PlayIcon size={26} kind="play" />
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            data-guide-arrow="prev"
            aria-label={t("site.manual.player.prev", {
              title: steps[Math.max(0, step - 1)]?.title,
            })}
            disabled={step <= 0}
            onClick={(event) => seekByArrow(event, -1)}
            sx={GUIDE_STYLES.arrow}
          >
            <ArrowIcon direction="prev" />
          </Box>
          <Box
            component="button"
            type="button"
            data-guide-arrow="next"
            aria-label={t("site.manual.player.next", {
              title: steps[Math.min(lastStep, step + 1)]?.title,
            })}
            disabled={step >= lastStep}
            onClick={(event) => seekByArrow(event, 1)}
            sx={GUIDE_STYLES.arrow}
          >
            <ArrowIcon direction="next" />
          </Box>
        </Box>
        <Box
          ref={navRef}
          sx={GUIDE_STYLES.nav}
          onPointerLeave={() => setPeekShown(false)}
        >
          <Box
            component="ol"
            sx={GUIDE_STYLES.segments}
            aria-label={t("site.manual.player.stepsLabel")}
          >
            {steps.map((item, index) => (
              <li
                key={index}
                data-done={index < step ? "" : undefined}
                aria-current={index === step ? "step" : undefined}
                onPointerEnter={(event) => showPeek(event, index)}
              >
                <button
                  type="button"
                  aria-label={t("site.manual.player.step", {
                    n: index + 1,
                    title: item.title,
                  })}
                  onClick={() => engineRef.current?.seek(index)}
                >
                  <i>
                    <b data-guide-bar="" />
                  </i>
                </button>
              </li>
            ))}
          </Box>
          <Box
            sx={GUIDE_STYLES.peek}
            data-on={peekShown ? "" : undefined}
            aria-hidden="true"
            style={peek ? { left: peek.left } : undefined}
          >
            {peek && peekView ? (
              <>
                <Box sx={GUIDE_STYLES.peekImage}>
                  {/* eslint-disable-next-line @next/next/no-img-element -- 사이트 미디어는 파이프라인이 만든 해시 파일을 그대로 쓴다(next/image 최적화 대상 아님) */}
                  <img
                    src={peek.url}
                    alt=""
                    decoding="async"
                    style={peekView.image}
                  />
                  {peekView.box ? <i style={peekView.box} /> : null}
                </Box>
                <Box component="p" sx={GUIDE_STYLES.peekCaption}>
                  <b>{peek.index + 1}</b>
                  <span>{steps[peek.index]?.title}</span>
                </Box>
              </>
            ) : null}
          </Box>
        </Box>
      </Box>
      <Box sx={GUIDE_STYLES.foot}>
        <Box component="p" sx={GUIDE_STYLES.caption}>
          <Box component="b" sx={GUIDE_STYLES.number}>
            {step + 1}
          </Box>
          <Box component="span" sx={GUIDE_STYLES.count}>
            / {steps.length}
          </Box>
          <Box component="span" sx={GUIDE_STYLES.title}>
            {steps[step]?.title}
          </Box>
        </Box>
        <Box
          component="button"
          type="button"
          aria-label={playing ? pauseLabel : playLabel}
          onClick={() => engineRef.current?.toggle(false)}
          sx={GUIDE_STYLES.toggle}
        >
          <span data-label="pause">
            <PauseIcon size={14} />
            {pauseLabel}
          </span>
          <span data-label="play">
            <PlayIcon size={14} />
            {playLabel}
          </span>
        </Box>
      </Box>
      {aside ? <Box sx={GUIDE_STYLES.aside}>{aside}</Box> : null}
      <Box component="ol" sx={GUIDE_STYLES.stepList}>
        {steps.map((item, index) => (
          <li key={index}>
            {item.title}. {item.caption}
          </li>
        ))}
      </Box>
    </Box>
  );
}
